import { AddressAutofill } from '@mapbox/search-js-react'
import { Theme } from '@mapbox/search-js-web'
import * as React from 'react'
import { HashFields, HiddenField, Label, Select, TextField } from 'react-rails-form-helpers'
import * as API from '../lib/api'

interface IProps {
  address: API.IAddress
  addressType: string
  stateSelectOptions: string[][]
  countrySelectOptions: string[][]
  toggleUseDefaultBillingAddress: () => void
  allowUsingDefault: boolean
  onAddressChanged: (field: AddressFormFields, value: string) => void
  // Optional section heading. The control form passes a title ("Billing
  // Address" / "Account Information"); the redesign embeds this form under a
  // step header it already renders, so it omits the title to avoid a second,
  // competing <h2>.
  title?: string
  mapbox?: API.IMapbox
  onAddressFieldsChanged?: (fields: Partial<API.IAddress>) => void
}

export enum AddressFormFields {
  Address1 = 'address1',
  Address2 = 'address2',
  City = 'city',
  State = 'state',
  Province = 'province',
  Zip = 'zip',
  Country = 'country',
}

// The fields this form marks `required`, and so the set its host's native
// constraint validation gates on. Lives here so the country-dependent
// state/province switch is stated once.
export function hasRequiredAddressFields(address: API.IAddress): boolean {
  const region = address.country === 'USA' ? address.state : address.province

  return Boolean(address.address1 && address.city && address.zip && address.country && region)
}

export class AddressForm extends React.Component<IProps, object> {
  // Bumped only on programmatic address syncs — a retrieve, or a native fill
  // that resolves a country change — so the keyed selects remount to show the
  // new value, and never on a user's own change (which would drop focus).
  private autofillSyncCount = 0

  private autofillEnabled() {
    const { mapbox, onAddressFieldsChanged } = this.props

    return Boolean(mapbox && mapbox.access_token.trim() && onAddressFieldsChanged)
  }

  // Mapbox's native fill writes raw values (alpha-2 country codes, unmapped
  // region names) into any element whose autocomplete slot it recognizes,
  // blanking a <select> keyed by other values and firing a change event that
  // corrupts shell state. Under autofill `handleRetrieve` syncs the selects
  // instead, so they must not advertise a slot Mapbox binds to.
  private selectAutoComplete(slot: string) {
    return this.autofillEnabled() ? undefined : `${this.props.addressType} ${slot}`
  }

  // Mapbox styles the suggestion popup (`.Results`) from these CSS variables:
  // --unit is the base font-size, and --fontFamily/--fontWeight/--lineHeight
  // map straight through. Match the app's form-field text so the dropdown
  // doesn't read as a foreign widget. cssText is scoped to the popup; here it
  // plays down the required "Powered by Mapbox" footer (`.ResultsAttribution`)
  // — dimmed and smaller — and drops the underline Mapbox adds on link hover.
  private autofillTheme(): Theme {
    return {
      variables: {
        fontFamily: 'Mulish, sans-serif',
        unit: '14px',
        fontWeight: '400',
        lineHeight: '21px',
      },
      cssText: [
        '.ResultsAttribution { opacity: 0.6; font-size: 11px; }',
        '.ResultsAttribution a:hover { text-decoration: none; }',
      ].join(' '),
    }
  }

  private sectionRef = React.createRef<HTMLElement>()
  private pendingNativeFields: { [field: string]: string } | null = null

  public componentDidMount() {
    if (this.autofillEnabled() && this.sectionRef.current) {
      this.sectionRef.current.addEventListener('change', this.handleNativeChange)
    }
  }

  public componentWillUnmount() {
    if (this.sectionRef.current) {
      this.sectionRef.current.removeEventListener('change', this.handleNativeChange)
    }
  }

  // Mapbox street-accuracy selections (and browser/extension autofills) write
  // text inputs natively with no retrieve event, so React never hears about
  // them. Native `change` fires only on a committed edit or a programmatic
  // write, not per keystroke, so this stays quiet during normal typing.
  private handleNativeChange = (e: Event) => {
    const target = e.target as HTMLInputElement
    if (target.tagName !== 'INPUT') { return }

    const autocompleteSlot = (target.getAttribute('autocomplete') || '').split(' ').pop()
    if (autocompleteSlot === 'country') {
      const country = this.props.mapbox && this.props.mapbox.country_map[target.value.toUpperCase()]
      if (country && country !== this.props.address.country) {
        this.queueNativeField('country', country)
      }
      return
    }

    const match = (target.name || '').match(/\[(address1|address2|city|zip|province)\]/)
    if (!match) { return }

    const field = match[1]
    // Native writes land synchronously in one burst and coalesce into a single
    // batched update, so after a full retrieve (whose batched update runs
    // first) every field already matches; dropping those keeps the burst from
    // triggering a second update — and a second tax recalc.
    if (this.props.address[field as keyof API.IAddress] === target.value) { return }

    this.queueNativeField(field, target.value)
  }

  private queueNativeField(field: string, value: string) {
    if (!this.pendingNativeFields) {
      this.pendingNativeFields = {}
      Promise.resolve().then(this.flushNativeFields)
    }
    this.pendingNativeFields[field] = value
  }

  private flushNativeFields = () => {
    const fields = this.pendingNativeFields
    this.pendingNativeFields = null

    if (fields && Object.keys(fields).length > 0 && this.props.onAddressFieldsChanged) {
      // A programmatic country change must remount the keyed selects, exactly
      // as a retrieve does, or the uncontrolled select keeps its old value.
      if (fields.country) { this.autofillSyncCount += 1 }
      this.props.onAddressFieldsChanged(fields as Partial<API.IAddress>)
    }
  }

  public isUSA() {
    const { address } = this.props
    const { country } = address

    return country === 'USA'
  }

  public stateSelectOptions() {
    const { stateSelectOptions } = this.props

    const stateOptions = stateSelectOptions.map(([_, value]) => {
      return <option key={value} value={value}>{value}</option>
    })

    return (stateOptions)
  }

  public countrySelectOptions() {
    const { countrySelectOptions } = this.props

    const countryOptions = countrySelectOptions.map(([name, value], index) => {
      return <option key={`${value}-${index}`} value={value}>{name}</option>
    })

    return (countryOptions)
  }

  public stateOrProvince() {
    const { address, addressType, onAddressChanged } = this.props
    const { state, province } = address

    if (this.isUSA()) {
      return (
        <React.Fragment>
          <Label htmlFor="state">State</Label>
          {/* Uncontrolled `defaultValue` ignores prop changes; the key forces a
              remount so autofill's programmatic update reaches the DOM. */}
          <Select
            key={this.autofillEnabled() ? `state-${this.autofillSyncCount}` : undefined}
            name="state"
            autoComplete={this.selectAutoComplete('address-level1')}
            required={true}
            defaultValue={state}
            data-1p-ignore="true"
            onChange={(e) => onAddressChanged(AddressFormFields.State, e.target.value)}
          >
            <option value=""/>
            {this.stateSelectOptions()}
          </Select>
        </React.Fragment>
      )
    }

    return (
      <React.Fragment>
        <Label htmlFor="province">Province</Label>
        <TextField
          name="province"
          autoComplete={`${addressType} address-level1`}
          required={true}
          defaultValue={province}
          data-1p-ignore="true"
          onChange={(e) => onAddressChanged(AddressFormFields.Province, e.target.value)}
        />
      </React.Fragment>
    )
  }

  public handleRetrieve = (res: any) => {
    const { mapbox, onAddressFieldsChanged, address } = this.props
    if (!mapbox || !onAddressFieldsChanged) { return }

    const feature = res && res.features && res.features[0]
    if (!feature) { return }

    const properties = feature.properties || {}
    const fields: Partial<API.IAddress> = {}

    // Mapbox emits ISO alpha-2; the country `<Select>` is keyed by alpha-3.
    const country = mapbox.country_map[(properties.country_code || '').toUpperCase()]
    if (country) { fields.country = country }

    if (properties.address_line1 !== undefined) { fields.address1 = properties.address_line1 }
    if (properties.address_line2 !== undefined) { fields.address2 = properties.address_line2 }
    if (properties.address_level2 !== undefined) { fields.city = properties.address_level2 }
    if (properties.postcode !== undefined) { fields.zip = properties.postcode }

    const region = properties.address_level1 || ''
    if (region) {
      if ((country || address.country) === 'USA') {
        fields.state = this.stateAbbrev(region)
      } else {
        fields.province = region
      }
    }

    if (Object.keys(fields).length === 0) { return }

    this.autofillSyncCount += 1
    onAddressFieldsChanged(fields)
  }

  // Mapbox emits the region as a short code (`CA` / `US-CA`) or a full name
  // (`California`); the state `<Select>` is keyed by abbreviation.
  private stateAbbrev(region: string): string {
    const { stateSelectOptions } = this.props
    const bare = region.replace(/^US-/, '').toUpperCase()

    const byValue = stateSelectOptions.find(([_, value]) => value.toUpperCase() === bare)
    if (byValue) { return byValue[1] }

    const byName = stateSelectOptions
      .find(([name]) => name.toLowerCase() === region.toLowerCase())

    return byName ? byName[1] : ''
  }

  // Street-accuracy selections never fire retrieve, but Mapbox's native fill
  // still writes the raw alpha-2 code into any country-slot TEXT input. This
  // probe is that slot: nameless so it never posts, and visually hidden —
  // `display` must stay non-none or Mapbox's visibility scan skips it.
  public renderCountryCodeProbe() {
    return (
      <input
        type="text"
        aria-hidden="true"
        tabIndex={-1}
        readOnly={true}
        autoComplete={`${this.props.addressType} country`}
        defaultValue=""
        data-1p-ignore="true"
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1, opacity: 0 }}
      />
    )
  }

  public renderCountryField() {
    const { address, onAddressChanged } = this.props
    const { country } = address

    return (
      <React.Fragment>
        <Label htmlFor="country">Country</Label>
        <Select
          key={this.autofillEnabled() ? `country-${this.autofillSyncCount}` : undefined}
          name="country"
          autoComplete={this.selectAutoComplete('country')}
          required={true}
          defaultValue={country}
          data-1p-ignore="true"
          onChange={(e) => onAddressChanged(AddressFormFields.Country, e.target.value)}
        >
          {this.countrySelectOptions()}
        </Select>
      </React.Fragment>
    )
  }

  public renderAddressFields() {
    const {
      address,
      addressType,
      onAddressChanged,
    } = this.props
    const {
      address1,
      address2,
      city,
      zip,
    } = address

    return (
      <HashFields name={`${addressType}_address`}>
        {/* Autofill resolves the country from the selected suggestion, so the
            picker trails the fields it would otherwise gate. Control keeps
            country first. */}
        {this.autofillEnabled() ? null : this.renderCountryField()}

        <Label htmlFor="address1">Street Address 1</Label>
        <TextField
          name="address1"
          autoComplete={`${addressType} address-line1`}
          required={true}
          defaultValue={address1}
          data-1p-ignore="true"
          onBlur={(e) => onAddressChanged(AddressFormFields.Address1, e.target.value)}
        />

        <Label htmlFor="address2">Street Address 2</Label>
        <TextField
          name="address2"
          autoComplete={`${addressType} address-line2`}
          defaultValue={address2}
          data-1p-ignore="true"
          onBlur={(e) => onAddressChanged(AddressFormFields.Address2, e.target.value)}
        />

        <div className="grid-x">
          <div className="cell small-full large-24 large-padded">
            <Label htmlFor="city">City</Label>
            <TextField
              name="city"
              autoComplete={`${addressType} address-level2`}
              required={true}
              defaultValue={city}
              data-1p-ignore="true"
              onBlur={(e) => onAddressChanged(AddressFormFields.City, e.target.value)}
            />
          </div>

          <div className="cell small-full large-36 large-padded">
            <div className="grid-x">
              <div className="cell small-30 large-33 small-padded">
                {this.stateOrProvince()}
              </div>

              <div className="cell small-30 large-27 small-padded">
                <Label htmlFor="zip">{this.isUSA() ? 'Zip Code' : 'Postal Code'}</Label>
                <TextField
                  name="zip"
                  autoComplete={`${addressType} postal-code`}
                  required={true}
                  defaultValue={zip}
                  data-1p-ignore="true"
                  onBlur={(e) => onAddressChanged(AddressFormFields.Zip, e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {this.autofillEnabled() ? this.renderCountryCodeProbe() : null}
        {this.autofillEnabled() ? this.renderCountryField() : null}
      </HashFields>
    )
  }

  public render() {
    const {
      addressType,
      allowUsingDefault,
      toggleUseDefaultBillingAddress,
      title,
      mapbox,
    } = this.props

    const fields = this.renderAddressFields()

    return (
      <section className="address-form" ref={this.sectionRef}>
        {title ? <h2>{title}</h2> : null}

        {this.autofillEnabled() && mapbox ? (
          <AddressAutofill
            accessToken={mapbox.access_token}
            onRetrieve={this.handleRetrieve}
            theme={this.autofillTheme()}
          >
            {fields}
          </AddressAutofill>
        ) : fields}

        <HiddenField name={`use_default_${addressType}_address`} value={false} />

        {allowUsingDefault ? <a onClick={() => toggleUseDefaultBillingAddress()}>Cancel</a> : null}
      </section>
    )
  }
}
